/**
 * FollowUpReportModal v120.4.1 — Chat Dialogue Voice/Text Input
 * 
 * Full transcription flow matching DescribeVoice:
 *   1. Orella greeting bubble
 *   2. AI-generated last visit summary card (from AVS)
 *   3. Orella prompt bubble
 *   4. Voice recorder → live transcription via Amazon Transcribe streaming
 *      - Recording state: waveform + pause/stop controls
 *      - Transcribing state: 3-step progress (uploading → transcribing → enhancing)
 *      - Complete state: playback bar + meta pills + editable transcript card
 *   5. Text fallback option
 *
 * Uses production OrellaChatBubble specs exactly:
 *   Logo: 36px, borderRadius 10px
 *   Bubble: bg #E9F8FF, border #C7E5FD, borderRadius 2px 16px 16px 16px
 *   Text: 13.5px, fontWeight 500, lineHeight 1.55
 *   Animation: logo delay+200ms, bubble delay+400ms, text delay+1200ms
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, Mic, Square, Pause, Play, Check, X, Upload, FileText, Sparkles, Globe, ShieldCheck, Eye, Pencil, RotateCcw, Headphones, Send } from 'lucide-react'
import { SmallOrellaBubble, AnimatedCard, ChatKeyframes } from '../../components/OrellaChatHeader'
import { transcriptionAPI, aiAPI } from '../../services/api'
import { startTranscribeStream } from '../../lib/transcribeStream'
import { uploadToStorage } from '../../services/storage'
import FollowUpQuestionsSection, { categorizeFollowUpAnswers, STANDARD_FOLLOWUP_QUESTIONS } from './FollowUpQuestionsSection'
import orellaOIcon from '../../assets/logos/orella_o_icon.png'
import { useI18n } from '../../i18n'
// v242.68.0 (FU-VOICE-PARITY-1). This screen carried a recorder that never
// moved past the Whisper era: 36 bars driven by Math.random() (no mic input at
// all), a red countdown, and the three-step Whisper progress panel shown even
// on the streaming path, where nothing uploads and nothing is transcribed
// after the fact. All four are now the same shared surface Describe uses.
import {
  VoiceRecorderKeyframes, useMicLevel, RecorderIdleHero, RecorderLivePanel,
  TidyingPanel,
} from '../../components/voice/VoiceRecorder'
import {
  isTooShortToTranscribe, streamUnavailableMessage, nothingRecordedMessage,
} from '../../components/voice/transcriptionOutcome'
import { micFailureMessage } from '../../components/voice/transcriptionOutcome'
import { isNativeApp } from '../../utils/authStorage'
import { DIALOGUE_BUBBLE, DIALOGUE_PAD } from '../../components/agent/dialogueBubble'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner
import FollowUpScaleSection from './FollowUpScaleSection' // s128: board D
import { scaleComplete, followUpScaleEntry } from '../../utils/followUpScales'
import { useQuestionnaireTranslation } from '../../i18n/questionnaires/useQuestionnaireTranslation'
import scalesLib from '../../data/scales.json'
import { scaleText } from '../../utils/scaleOffer'

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE_BLUE = '#E9F8FF'
const GREEN_DARK = '#16A34A'
const GREEN_LIGHT = '#D5EFD3'

const MAX_RECORDING_TIME = 180 // 3 minutes

/**
 * Inline OrellaBubble with typing animation — uses production specs
 */
function InlineBubble({ message, visible, delay = 0, size = 'large', onComplete }) {
  const { t } = useI18n()
  const [showLogo, setShowLogo] = useState(false)
  const [showBubble, setShowBubble] = useState(false)
  const [showDots, setShowDots] = useState(false)
  const [showText, setShowText] = useState(false)
  const calledRef = useRef(false)

  useEffect(() => {
    if (!visible) return
    const t = []
    t.push(setTimeout(() => setShowLogo(true), delay + 200))
    t.push(setTimeout(() => { setShowBubble(true); setShowDots(true) }, delay + 400))
    t.push(setTimeout(() => { 
      setShowDots(false); setShowText(true)
      if (!calledRef.current) { calledRef.current = true; onComplete?.() }
    }, delay + 1200))
    return () => t.forEach(clearTimeout)
  }, [visible, delay])

  if (!visible) return null
  const sm = size === 'small'

  return (
    <div className="px-4 pt-3 pb-1">
      <div className="flex items-start gap-2.5">
        <img 
          src={orellaOIcon} alt="O"
          className="w-9 h-9 rounded-lg flex-shrink-0 object-contain transition-all duration-[400ms]"
          style={{ opacity: showLogo ? 1 : 0, transform: showLogo ? 'scale(1)' : 'scale(0.75)' }}
        />
        <div 
          className={`flex-1 ${DIALOGUE_BUBBLE} ${DIALOGUE_PAD} transition-all duration-[400ms]`}
          style={{
            opacity: showBubble ? 1 : 0,
            transform: showBubble ? 'translateY(0) scale(1)' : 'translateY(4px) scale(0.95)',
          }}
        >
          {showDots && !showText && (
            <span className="inline-flex items-center gap-1 h-5">
              {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite]" style={{ animationDelay: `${i*0.2}s` }} />)}
            </span>
          )}
          {showText && (
            <p className="text-[15.5px] font-medium text-[#15354F] leading-relaxed animate-[fadeIn_0.3s_ease]">
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Meta pill — confidence, language, AI badge
 */
function MetaPill({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium" style={{ backgroundColor: ICE_BLUE, color: BLUE }}>
      <Icon size={10} />{label}
    </span>
  )
}

export default function FollowUpReportModal({ 
  open, onClose, onSubmit, 
  lastVisitSummary, lastVisitDate, lastVisitDoctor, recapLoading,
  // v173.0.0: Unified follow-up report props
  caseId, encounterId, isFollowUp, language: caseLang,
  // ★★★ CHECKUP-MODAL-REUSE-1 (session 91). Two props, not a component.
  //   includeVoice — a monitored patient does not owe anyone a weekly voice memo.
  //   bridgeType   — the question set is chosen by WHAT IS BEING ASKED, not by WHICH
  //                  FLOW IS ASKING. It was a typed constant here while the same
  //                  file's other two fetches picked it from case status.
  //   Both defaults preserve the behaviour every existing caller had.
  includeVoice = true, bridgeType = 'follow_up_checkin',
  initialVoiceData, initialUnifiedQuestions, initialUnifiedAnswers, initialUnifiedDetails,
  // s128 (board D): the confirmed in-app scales [{ order, def }] (utils/followUpScales) — asked after the voice memo.
  scaleItems = [],
}) {
  const { t, language } = useI18n()
  const { tq } = useQuestionnaireTranslation()
  const tScaleLib = scaleText(scalesLib, language)
  const tS = (s) => { const v = tScaleLib(s); return v !== s ? v : tq(s) }
  const [scaleAnswers, setScaleAnswers] = useState({}) // s128: { [scale]: the ScoredScale line }
  const [scalesDone, setScalesDone] = useState(false)
  const scalesReady = scaleItems.every(({ def }) => scaleComplete(def, scaleAnswers[def.scale]))
  const ts = (key, fallback) => { const v = t(key); return (!v || v === key) ? fallback : v }
  // === Chat dialogue step (0=greeting, 1=summary+prompt visible) ===
  const [step, setStep] = useState(0)

  // === Voice state machine ===
  //   idle | recording | paused | tidying | complete | text-only
  // v242.68.0: 'tidying' is the whole point. The streaming path used to sit in
  // 'transcribing' with the step forced to 3, which rendered an
  // upload/transcribe/enhance panel over work that was not happening.
  // ★ 'transcribing' IS GONE (WHISPER-REMOVAL-1). It described the batch round
  // trip, and nothing sets it any more. Two disabled-guards further down still
  // name it; they are harmless and always false, and are left rather than
  // re-derived in the same pass that deleted the engine.
  const [voiceState, setVoiceState] = useState('idle')
  const [recordingTime, setRecordingTime] = useState(0)
  const recordingTimeRef = useRef(0)
  const [transcript, setTranscript] = useState('')
  const [textInput, setTextInput] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [error, setError] = useState(null)
  
  // Meta info
  const [confidence, setConfidence] = useState(null)
  const [detectedLang, setDetectedLang] = useState(null)
  
  // Playback
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackTime, setPlaybackTime] = useState(0)
  const audioElementRef = useRef(null)
  const audioUrlRef = useRef(null)

  // Recording refs
  const timerRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const audioBlobRef = useRef(null)
  // v242.59.0 (AI-2 surface 4/6 + FU-AUDIO-1)
  const streamSessionRef = useRef(null)
  const streamCommittedRef = useRef('')
  const storagePathRef = useRef(null)
  const [wasTidied, setWasTidied] = useState(false)
  // v242.68.0 — parity with Describe: the real-amplitude meter, the live
  // "I can hear you" signal, and the verbatim text behind the tidy pass.
  const { level, attach: attachMicLevel, detach: detachMicLevel } = useMicLevel('FollowUpReport')
  const [heardSpeech, setHeardSpeech] = useState(false)
  const [rawTranscript, setRawTranscript] = useState('')
  const [showOriginal, setShowOriginal] = useState(false)
  const textareaRef = useRef(null)
  const scrollRef = useRef(null)

  // v173.0.0: Unified follow-up questions state (post-voice phase)
  // ★ With includeVoice=false there is no voice gate to clear, so the modal opens
  //   straight into generation. See the effect below that fires it.
  const [reportPhase, setReportPhase] = useState(includeVoice ? 'voice' : 'generating') // 'voice' | 'generating' | 'questions' | 'submitting'
  const [unifiedQuestions, setUnifiedQuestions] = useState(initialUnifiedQuestions || null)
  const [unifiedQuestionsLoading, setUnifiedQuestionsLoading] = useState(false)
  const [unifiedQuestionsError, setUnifiedQuestionsError] = useState(null)
  const [unifiedAnswers, setUnifiedAnswers] = useState(initialUnifiedAnswers || {})
  const [unifiedDetails, setUnifiedDetails] = useState(initialUnifiedDetails || {})
  const questionsFetchedRef = useRef(false)

  // Generate unified questions after voice gate clears
  const generateQuestions = async (voiceTranscript) => {
    if (questionsFetchedRef.current || !caseId || !isFollowUp) return
    questionsFetchedRef.current = true
    setReportPhase('generating')
    setUnifiedQuestionsLoading(true)
    setUnifiedQuestionsError(null)

    try {
      // ★★★ WAS `const bridgeType = 'follow_up_checkin'` — a typed constant. It is the
      //   prop now, so an observation case asks observation questions.
      const result = await aiAPI.generateBridgeQuestions(caseId, encounterId, bridgeType, caseLang || language || 'en', voiceTranscript || '')
      if (result?.questions?.length >= 2) {
        setUnifiedQuestions(result.questions)
        setReportPhase('questions')
        console.log(`[FollowUpReport] ✅ Unified questions: ${result.questions.length} Qs`)
      } else {
        // v217.1.5: AI returned no/insufficient questions — substitute the
        // standard check-in set so the user can still complete the report.
        // The soft amber banner in FollowUpQuestionsSection explains the
        // substitution to the user (per copy: "We'll use standard check-in
        // questions instead").
        console.warn('[FollowUpReport] AI returned insufficient questions — using standard fallback set')
        setUnifiedQuestions(STANDARD_FOLLOWUP_QUESTIONS)
        setUnifiedQuestionsError('insufficient')
        setReportPhase('questions')
      }
    } catch (err) {
      // v217.1.5: Same fallback substitution on hard errors (5xx, timeout,
      // network). The user is never stranded — they get a standard set and
      // a "Try again" link to retry AI generation.
      console.warn('[FollowUpReport] Question generation failed — using standard fallback set:', err?.message)
      setUnifiedQuestions(STANDARD_FOLLOWUP_QUESTIONS)
      setUnifiedQuestionsError(err?.message || 'Failed')
      setReportPhase('questions')
    } finally {
      setUnifiedQuestionsLoading(false)
    }
  }

  // ★★ No voice leg means nothing else will ever call generateQuestions: handleSave is
  //   the voice path's exit and it is not rendered. Fire it on open, with no transcript.
  useEffect(() => {
    if (!open || includeVoice) return
    generateQuestions('')
  }, [open, includeVoice, caseId])

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setStep(0); setVoiceState('idle'); setRecordingTime(0); recordingTimeRef.current = 0
      setTranscript(''); setTextInput(''); setIsEditing(false)
      setError(null); setConfidence(null); setDetectedLang(null)
      setIsPlaying(false); setPlaybackTime(0)
      audioChunksRef.current = []
      audioBlobRef.current = null
      if (audioElementRef.current) { audioElementRef.current.pause(); audioElementRef.current = null }
      if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null }
      clearInterval(timerRef.current)
    }
  }, [open])

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  // ═══════════════════════════════════════════════════════════════════════
  // RECORDING
  // ═══════════════════════════════════════════════════════════════════════

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 }
      })
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'

      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder
      audioChunksRef.current = []

      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data) }

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType })
        audioBlobRef.current = audioBlob
        if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current)
        audioUrlRef.current = URL.createObjectURL(audioBlob)

        const finalDuration = recordingTimeRef.current

        // Close the socket BEFORE killing the tracks so a final committed
        // segment can drain; then release the shared capture.
        const session = await streamSessionRef.current
        try { await session?.stop?.() } catch (e) { /* noop */ }
        streamSessionRef.current = null
        detachMicLevel()
        stream.getTracks().forEach(track => track.stop())

        // v242.59.0 (FU-AUDIO-1) — the audio goes to S3 and only its PATH is
        // submitted. This previously sent URL.createObjectURL(...) — a `blob:`
        // URL that existed only in this browser tab — which was then persisted
        // to voice_audio_url and rendered by the MD panel as a "Listen to
        // recording" link that could never resolve for anyone.
        const streamed = (streamCommittedRef.current || '').trim()

        // TF-23, and it bites harder on this screen than on Describe: the
        // upload used to run FIRST, so a stray mic tap wrote a few hundred
        // bytes of nothing into the PHI bucket before anyone asked whether it
        // held speech. Bail before the upload, not after it.
        if (!streamed && isTooShortToTranscribe(audioBlob, finalDuration)) {
          console.warn('[FollowUpReport] capture too short to transcribe:', audioBlob.size, 'bytes /', finalDuration, 's')
          if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null }
          audioChunksRef.current = []
          audioBlobRef.current = null
          setError(nothingRecordedMessage(t, 'followUpReport'))
          setRecordingTime(0)
          recordingTimeRef.current = 0
          setVoiceState('idle')
          return
        }

        storagePathRef.current = await uploadFollowUpAudio(audioBlob, mimeType)

        if (streamed) {
          await completeFromStream(streamed, finalDuration)
          return
        }

        // Streaming yielded nothing and there is no second engine to appeal
        // to (WHISPER-REMOVAL-1). The recording is saved above; say what
        // happened and leave the patient the textarea.
        setError(streamUnavailableMessage(null, t, 'followUpReport'))
        setVoiceState('complete')
      }

      recorder.onerror = () => {
        setError(t('errors2.recordingFailed'))
        setVoiceState('idle')
      }

      // v242.59.0 — Transcribe streaming, and since WHISPER-REMOVAL-1 the only engine.
      // ONE getUserMedia capture is shared with the stream: a second concurrent
      // capture is unreliable on iOS.
      streamCommittedRef.current = ''
      storagePathRef.current = null
      setWasTidied(false)
      // ★ THE PROMISE GOES IN THE REF, NOT THE RESOLVED SESSION (FU-STREAM-MINT-1).
      // Deliberately NOT awaited: recording must start on the tap, not after a
      // network round trip for the presigned URL. But storing the session in a
      // .then() meant a recording SHORTER THAN THE MINT reached onstop with a
      // null ref — so stop() was never called, the stream was never drained,
      // the socket was left running, and the screen fell through to the Whisper
      // leg that is unconfigured by design (D48b) and always errors. Awaiting
      // the promise at stop time costs nothing when it has already resolved.
      streamSessionRef.current = startTranscribeStream({
        stream,
        mint: () => transcriptionAPI.streamUrl({
          surface: 'patient',
          language: (caseLang || language || 'en').slice(0, 2),
        }),
        onPartial: () => setHeardSpeech(true),
        onCommit: (text) => {
          if (text) streamCommittedRef.current = (streamCommittedRef.current + ' ' + text).trim()
          setHeardSpeech(true)
        },
        onError: (err) => console.warn('[FollowUpReport] stream error:', err?.name, err?.message),
        onClose: (i) => console.log('[FollowUpReport] stream closed', i),
      }).catch((err) => {
        console.warn('[FollowUpReport] streaming unavailable; the patient types instead:', err?.message)
        // Resolve to null rather than rejecting: the ref is awaited on the stop
        // path, and a rejected promise parked in a ref is an unhandled rejection
        // waiting for whoever reads it next.
        return null
      })

      recorder.start(1000)
      // Real amplitude off the SAME stream. Fail-soft: a dead meter renders its
      // floor and never takes the recording down with it.
      attachMicLevel(stream)
      setHeardSpeech(false)
      setVoiceState('recording')
      setRecordingTime(0)
      recordingTimeRef.current = 0
      setTranscript(''); setTextInput(''); setError(null)

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1
          recordingTimeRef.current = newTime
          if (newTime >= MAX_RECORDING_TIME) { stopRecording(); return prev }
          return newTime
        })
      }, 1000)
    } catch (err) {
      // v242.172.0 APL-3 — was two branches, so a patient with NO microphone was told to
      //   check their settings. The owner knows the difference.
      setError(micFailureMessage(err, t, 'followUpReport', isNativeApp()))
      setVoiceState('text-only')
    }
  }

  const stopRecording = () => {
    clearInterval(timerRef.current); timerRef.current = null
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
  }

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause()
      clearInterval(timerRef.current)
      setVoiceState('paused')
    }
  }

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume()
      setVoiceState('recording')
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1
          recordingTimeRef.current = newTime
          if (newTime >= MAX_RECORDING_TIME) { stopRecording(); return prev }
          return newTime
        })
      }, 1000)
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // TRANSCRIPTION (matches DescribeVoice pattern)
  // ═══════════════════════════════════════════════════════════════════════

  // v242.59.0 (FU-AUDIO-1) — store the recording by reference. Fail-soft, and
  // deliberately NOT fail-back-to-a-blob-URL: a failed upload costs the audio,
  // never the transcript. Persisting an unresolvable reference is the defect
  // this closes.
  const uploadFollowUpAudio = async (blob, mimeType) => {
    if (!caseId || !blob || !blob.size) return null
    try {
      const ext = String(mimeType).includes('mp4') ? 'mp4' : 'webm'
      const path = await uploadToStorage(
        caseId, `followup-note-${Date.now()}.${ext}`, blob, mimeType
      )
      if (!path) console.warn('[FollowUpReport] audio upload returned no path; transcript only')
      return path || null
    } catch (err) {
      console.warn('[FollowUpReport] audio upload failed; transcript only:', err?.message || err)
      return null
    }
  }

  // Streaming completion. The text already arrived live, so there is no upload
  // or transcription step to show — only the cosmetic tidy pass.
  //
  // Tidy is NOT optional here: the batch engine stripped disfluency at the ASR level and
  // Transcribe does not, so shipping streaming without it would make follow-up
  // transcripts visibly WORSE than they were. Every failure mode falls through
  // to the raw text silently.
  const completeFromStream = async (transcriptText, duration) => {
    setVoiceState('tidying')
    setError(null)
    setConfidence(null)     // streaming returns no confidence score
    setDetectedLang(null)

    let finalText = transcriptText
    let tidied = false
    try {
      const res = await transcriptionAPI.tidy(transcriptText)
      const candidate = (res?.tidied || '').trim()
      if (res?.wasTidied && candidate) { finalText = candidate; tidied = true }
      else if (res?.reason) console.warn('[FollowUpReport] tidy declined')
    } catch (err) {
      console.warn('[FollowUpReport] tidy failed, using raw transcript:', err?.message || err)
    }

    setTranscript(finalText)
    setTextInput(finalText)
    setRawTranscript(transcriptText)
    setShowOriginal(false)
    setWasTidied(tidied)
    setVoiceState('complete')
  }

  // startTranscription — REMOVED (WHISPER-REMOVAL-1). It posted the
  // recording as base64 to the batch endpoint and rendered whatever came
  // back. OPENAI_API_KEY has been absent from every secret group since
  // D48b, so what came back was always the configuration envelope and the
  // patient always saw an error. Amazon Transcribe streaming is the only
  // engine now; when it does not connect, the patient types.

  // ═══════════════════════════════════════════════════════════════════════
  // PLAYBACK
  // ═══════════════════════════════════════════════════════════════════════

  const handlePlayPause = () => {
    if (!audioElementRef.current) {
      audioElementRef.current = new Audio(audioUrlRef.current)
      audioElementRef.current.onended = () => { setIsPlaying(false); setPlaybackTime(0) }
      audioElementRef.current.ontimeupdate = () => setPlaybackTime(Math.floor(audioElementRef.current.currentTime))
    }
    if (isPlaying) { audioElementRef.current.pause(); setIsPlaying(false) }
    else { audioElementRef.current.play(); setIsPlaying(true) }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RE-RECORD
  // ═══════════════════════════════════════════════════════════════════════

  const handleReRecord = () => {
    if (audioElementRef.current) { audioElementRef.current.pause(); audioElementRef.current = null }
    if (audioUrlRef.current) { URL.revokeObjectURL(audioUrlRef.current); audioUrlRef.current = null }
    audioChunksRef.current = []; audioBlobRef.current = null
    setVoiceState('idle'); setRecordingTime(0); recordingTimeRef.current = 0
    setPlaybackTime(0); setIsPlaying(false)
    setTranscript(''); setTextInput(''); setIsEditing(false)
    setConfidence(null); setDetectedLang(null)
    setError(null)
    // v242.59.0 — a stale storage path or pill must never survive into the next
    // recording, and a leftover socket must not keep committing text.
    // DISCARD, and it must resolve the promise first: a socket whose mint had
    // not landed yet is exactly the one this line used to leave running.
    const pendingSession = streamSessionRef.current
    streamSessionRef.current = null
    Promise.resolve(pendingSession)
      .then((s) => s?.stop?.({ deliver: false }))
      .catch(() => { /* noop */ })
    streamCommittedRef.current = ''
    storagePathRef.current = null
    setWasTidied(false)
    detachMicLevel()
    setHeardSpeech(false)
    setRawTranscript('')
    setShowOriginal(false)
  }

  // ═══════════════════════════════════════════════════════════════════════
  // SUBMIT
  // ═══════════════════════════════════════════════════════════════════════

  // v173.0.0: For follow-up cases, voice save transitions to question phase
  // For non-follow-up (initial encounter), behaves as before — closes modal
  const handleSave = () => {
    const finalTranscript = (textInput || transcript).trim()
    const voiceData = {
      transcript: finalTranscript,
      duration: recordingTimeRef.current,
      hasAudio: audioChunksRef.current.length > 0,
      // v242.59.0 (FU-AUDIO-1): submit the STORAGE PATH, not the in-tab blob URL.
      audioUrl: storagePathRef.current || null,
      confidence: confidence || null,
      detectedLanguage: detectedLang || null,
    }
    
    if (isFollowUp) {
      // Save voice data to parent, then transition to questions phase
      onSubmit(voiceData, 'voice-only') // second param signals partial save
      generateQuestions(finalTranscript)
      // Scroll to bottom after a beat
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 500)
    } else {
      // Non-follow-up: just save and close (original behavior)
      onSubmit(voiceData)
    }
  }

  // v174.0.1: Final submit with voice + unified answers — signals DeliverScreen to submit directly
  const [reportSubmitting, setReportSubmitting] = useState(false)
  
  const handleSubmitReport = async () => {
    if (reportSubmitting) return
    setReportSubmitting(true)
    
    const finalTranscript = (textInput || transcript).trim()
    const categorized = categorizeFollowUpAnswers(unifiedQuestions, unifiedAnswers, unifiedDetails)
    
    onSubmit({
      transcript: finalTranscript,
      duration: recordingTimeRef.current,
      hasAudio: audioChunksRef.current.length > 0,
      // v242.59.0 (FU-AUDIO-1): submit the STORAGE PATH, not the in-tab blob URL.
      audioUrl: storagePathRef.current || null,
      confidence: confidence || null,
      detectedLanguage: detectedLang || null,
      followUpQuestions: {
        questions: unifiedQuestions,
        answers: unifiedAnswers,
        details: unifiedDetails,
        categorized,
        source: 'ai-unified',
        generatedAt: new Date().toISOString(),
      },
      symptomSurvey: Object.keys(categorized.symptom).length > 0 ? { ...categorized.symptom, _source: 'ai-unified' } : {},
      // s128: the follow-up scales → pre_visit_report.scales on this encounter (never over the Detect baseline)
      scales: scaleItems.filter(({ def }) => scaleComplete(def, scaleAnswers[def.scale]))
        .map(({ def, order }) => followUpScaleEntry(def, order, scaleAnswers[def.scale])),
      medicationReview: Object.keys(categorized.medication).length > 0 || Object.keys(categorized.side_effect).length > 0
        ? { ...categorized.medication, ...categorized.side_effect, _source: 'ai-unified' } : {},
    }, 'submit-report') // Signal DeliverScreen to submit to API immediately
  }

  if (!open) return null

  const hasSummary = !!(lastVisitSummary || recapLoading)
  const canSave = (textInput || transcript).trim() || (voiceState === 'complete' && audioUrlRef.current)

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-[#F8FAFC]" style={{ animation: 'fadeSlideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}>
      <ChatKeyframes />
      <VoiceRecorderKeyframes />
      <style>{`
        @keyframes fadeSlideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      {/* ★ v242.87.0 — same defect as TF b35 #28 ("Overlap in menu"), same file
          shape, third copy. A portaled full-screen column owns the whole screen,
          so MobileLayout's inset never reaches it and it must paint its own or
          the title sits under the clock. Canonical expression, byte-identical to
          CheckInModal.jsx / SymptomCheckModal.jsx. Gated by
          scripts/check-safe-area.mjs. */}
      <div className="flex items-center gap-2.5 px-4 pb-3 border-b border-gray-100 bg-white flex-shrink-0"
           data-safe-top="1"
           style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
        <button onClick={onClose} className="p-1 bg-transparent border-none cursor-pointer"><ArrowLeft size={22} style={{ color: NAVY }} /></button>
        <span className="text-[15px] font-bold" style={{ color: NAVY }}>{t('followUpReport.followupReport')}</span>
        <button onClick={onClose} className="p-1 ml-auto bg-transparent border-none cursor-pointer"><X size={22} className="text-gray-400" /></button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto pb-4" ref={scrollRef}>

        {/* 1. Last Visit Recap — opens immediately, no greeting bubble */}
        {/* v174.6.0: Removed "Let's prepare" bubble. Recap is the first thing shown. */}
        {(lastVisitSummary || recapLoading) && (
          <AnimatedCard visible={true} delay={200}>
            <div className="rounded-[16px] border-[1.5px] overflow-hidden" style={{ borderColor: '#A7D9FC', backgroundColor: '#F0F9FF' }}>
              <div className="p-4">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-[10px] bg-[#E9F8FF] border-[1.5px] border-[#A7D9FC] flex items-center justify-center">
                    <img src={orellaOIcon} alt="" className="w-5 h-5 rounded-md object-contain" />
                  </div>
                  <div>
                    <span className="text-[14px] font-bold" style={{ color: NAVY }}>{t('followUpReport.yourLastVisit')}</span>
                    <span className="text-[11px] text-gray-500 block">
                      {lastVisitDate || 'Previous visit'}{lastVisitDoctor ? ` · ${lastVisitDoctor}` : ''}
                    </span>
                  </div>
                </div>
                {recapLoading ? (
                  <div className="bg-white/60 rounded-[12px] p-3.5 flex items-center gap-2">
                    <OrellaSpinner size={16} className="flex-shrink-0" />
                    <span className="text-[13px] text-gray-400">{t('followUpReport.preparingYourVisitRecap')}</span>
                  </div>
                ) : (
                  <p className="text-[14px] text-[#334155] leading-[1.65] bg-white/60 rounded-[12px] p-3.5">
                    {lastVisitSummary}
                  </p>
                )}
              </div>
            </div>
          </AnimatedCard>
        )}

        {/* 2. Prompt to record — auto-shows after recap */}
        {/* ★★★ GATED, NOT HIDDEN. AnimatedCard with visible=false still MOUNTS its
            children, and the child here is the recorder — useMicLevel, MediaRecorder,
            the permission prompt. A monitored patient must not be asked for the
            microphone by a card they are not being shown. */}
        {includeVoice && (<>
        <InlineBubble visible={!recapLoading} delay={lastVisitSummary ? 800 : 200} onComplete={() => setStep(1)}
          message={ts('followUpReport.tellUsHowThings', 'Tell us how things have been since your last visit. Focus on how you\'re feeling, any changes, and new concerns.')} />

        {/* 3. Voice/Text Input Card */}
        <AnimatedCard visible={step >= 1} delay={400}>
          <div className="rounded-[14px] border-[1.5px] border-gray-200 bg-white overflow-hidden">

            {/* ═══════════ IDLE ═══════════ */}
            {(voiceState === 'idle' || voiceState === 'text-only') && (
              <div className="p-5">
                {voiceState === 'idle' && (
                  <div className="mb-4">
                    <RecorderIdleHero
                      ns="followUpReport"
                      onRecord={startRecording}
                      onType={() => {
                        setVoiceState('text-only')
                        setTimeout(() => textareaRef.current?.focus(), 80)
                      }}
                    />
                  </div>
                )}
                {voiceState === 'text-only' && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      {t('followUpReport.typeYourUpdate', 'Type your update')}
                    </p>
                    <textarea ref={textareaRef} value={textInput} onChange={e => setTextInput(e.target.value)}
                      placeholder={t('followUpReport.howHaveYouBeen', 'How have you been feeling since your last visit?')}
                      className="w-full h-[100px] border-[1.5px] border-gray-200 rounded-xl p-3 text-[13px] text-gray-700 resize-none focus:border-blue-300 focus:outline-none" />
                  </div>
                )}
              </div>
            )}

            {/* ═══════════ RECORDING / PAUSED ═══════════ */}
            {(voiceState === 'recording' || voiceState === 'paused') && (
              <div className="p-4">
                <RecorderLivePanel
                  ns="followUpReport"
                  state={voiceState}
                  seconds={recordingTime}
                  level={level}
                  heardSpeech={heardSpeech}
                  onPause={pauseRecording}
                  onResume={resumeRecording}
                  onStop={stopRecording}
                />
              </div>
            )}

            {/* ═══════════ TIDYING (streaming path) ═══════════ */}
            {voiceState === 'tidying' && (
              <div className="p-4">
                <TidyingPanel ns="followUpReport" />
              </div>
            )}

            {/* ═══════════ TRANSCRIBING ═══════════ */}
            {/* The 'transcribing' progress panel was removed with Whisper
                (WHISPER-REMOVAL-1). It showed a staged progress bar while the
                batch leg ran; nothing sets that state any more, because there is
                no round trip to narrate. Streaming produces text live. */}

            {/* ═══════════ COMPLETE ═══════════ */}
            {voiceState === 'complete' && (
              <div className="p-4 space-y-3">
                {/* Playback bar */}
                {audioUrlRef.current && (
                  <div className="bg-gray-50 rounded-xl p-3.5 flex items-center gap-3 border border-gray-100">
                    <button onClick={handlePlayPause}
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-none cursor-pointer"
                      style={{ background: `linear-gradient(135deg, ${BLUE}, #3A8FD6)`, boxShadow: `0 2px 8px ${BLUE}30` }}>
                      {isPlaying ? <Pause size={14} className="text-white" /> : <Play size={14} className="text-white ml-0.5" />}
                    </button>
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-[#46A8F9] transition-all"
                        style={{ width: `${recordingTime > 0 ? (playbackTime / recordingTime) * 100 : 0}%` }} />
                    </div>
                    <span className="text-xs font-medium text-gray-400 tabular-nums w-10 text-right">
                      {isPlaying ? fmt(playbackTime) : fmt(recordingTime)}
                    </span>
                  </div>
                )}

                {/* Meta pills */}
                {(detectedLang || confidence || wasTidied) && (
                  <div className="flex gap-1.5 flex-wrap">
                    {detectedLang && <MetaPill icon={Globe} label={detectedLang} />}
                    {confidence && <MetaPill icon={ShieldCheck} label={`${confidence}% confidence`} />}
                    {/* v242.68.0 — was an "AI Enhanced" badge. The tidy pass
                        removes disfluency; it does not enhance anything, and
                        naming it accurately is what lets the patient judge it. */}
                    {wasTidied && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium"
                        style={{ backgroundColor: '#F0FBF3', color: '#2E7D4F' }}
                      >
                        <Check size={10} />{t('followUpReport.fillerWordsRemoved', 'Filler words removed')}
                      </span>
                    )}
                  </div>
                )}

                {/* Unified editable card — no Edit toggle, always editable */}
                {(transcript || textInput) ? (
                  <div className="rounded-xl border-[1.5px] border-[#46A8F9] bg-blue-50/30 overflow-hidden">
                    <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-blue-100/60 bg-white/60">
                      <span className="text-[11px] font-bold text-[#15354F] uppercase tracking-wide">
                        {t('followUpReport.yourStory', 'Your story')}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {(textInput || transcript).length} {t('describe.characters', 'characters')}
                      </span>
                    </div>
                    <textarea
                      ref={textareaRef}
                      value={textInput || transcript}
                      onChange={e => {
                        setTextInput(e.target.value)
                        setTranscript(e.target.value)
                        // Once the patient edits, "filler words removed"
                        // describes text that no longer exists. Retract it.
                        if (wasTidied) { setWasTidied(false); setShowOriginal(false) }
                      }}
                      onInput={e => {
                        const ta = e.currentTarget
                        ta.style.height = 'auto'
                        ta.style.height = ta.scrollHeight + 'px'
                      }}
                      className="w-full min-h-[130px] p-3.5 text-[13px] leading-relaxed bg-transparent border-none outline-none resize-none overflow-hidden text-[#15354F]"
                    />

                    {wasTidied && rawTranscript && (
                      <div className="px-3.5 py-2.5 border-t border-blue-100/60 bg-white/50">
                        <button
                          onClick={() => setShowOriginal(v => !v)}
                          className="inline-flex items-center gap-1.5 text-[11.5px] font-bold bg-transparent border-none cursor-pointer"
                          style={{ color: BLUE }}
                        >
                          <Eye size={12} />
                          {showOriginal
                            ? t('followUpReport.hideOriginal', 'Hide what I actually said')
                            : t('followUpReport.seeOriginal', 'See what I actually said')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  /* No transcript (transcription failed) — show textarea */
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      {t('followUpReport.recordingSavedAddNotes', 'Recording saved — add notes below')}
                    </p>
                    <textarea value={textInput} onChange={e => setTextInput(e.target.value)}
                      placeholder={t('followUpReport.typeWhatYouToldUs', 'Type what you told us, or add any additional notes...')}
                      className="w-full h-[100px] border-[1.5px] border-gray-200 rounded-xl p-3 text-[13px] text-gray-700 resize-none focus:border-blue-300 focus:outline-none" />
                  </div>
                )}

                {wasTidied && showOriginal && rawTranscript && (
                  <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#E8EDF2', background: '#FBFCFD' }}>
                    <div className="px-3.5 py-2.5 border-b bg-white" style={{ borderColor: '#E8EDF2' }}>
                      <span className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: '#64748B' }}>
                        {t('followUpReport.whatYouSaid', 'What you actually said')}
                      </span>
                    </div>
                    <p className="p-3.5 text-[13px] leading-relaxed" style={{ color: '#64748B' }}>{rawTranscript}</p>
                  </div>
                )}

                {/* Re-record */}
                <button onClick={handleReRecord}
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-red-500 transition-colors mx-auto bg-transparent border-none cursor-pointer">
                  <RotateCcw size={12} /> {t('followUpReport.reRecord')}
                </button>
              </div>
            )}
          </div>
        </AnimatedCard>
        </>)}

        {/* Error message */}
        {error && (
          <div className="mx-4 mt-2 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
            <X size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-[12px] text-red-600">{error}</p>
          </div>
        )}

        {/* v173.0.0: Post-voice phase — generating spinner + questions */}
        {/* s128 (board D1 + D2): the clinician's confirmed scales, after the voice memo and before the questions */}
        {isFollowUp && reportPhase !== 'voice' && scaleItems.length > 0 && !scalesDone && (
          <>
            <SmallOrellaBubble visible={true} delay={100}
              message={t('deliver.scaleAgainBubble', "Your doctor asked for one score again, so they can see how you've changed since your first visit.")} />
            <AnimatedCard visible={true} delay={400}>
              <FollowUpScaleSection items={scaleItems} answers={scaleAnswers} doctor={lastVisitDoctor || ''} tS={tS}
                onChange={(scale, v) => setScaleAnswers((a) => ({ ...a, [scale]: v }))} />
            </AnimatedCard>
          </>
        )}

        {isFollowUp && reportPhase !== 'voice' && (scaleItems.length === 0 || scalesDone) && (
          <>
            {/* Generating spinner */}
            {reportPhase === 'generating' && (
              <AnimatedCard visible={true} delay={200}>
                <FollowUpQuestionsSection
                  questions={null}
                  loading={true}
                  error={null}
                />
              </AnimatedCard>
            )}

            {/* Orella bubble introducing questions */}
            {reportPhase === 'questions' && unifiedQuestions?.length > 0 && (
              <SmallOrellaBubble visible={true} delay={100}
                message={ts('deliver.fewQuestions', 'We have a few questions about how things have been going since your last appointment.')} />
            )}

            {/* Questions section */}
            {reportPhase === 'questions' && (
              <AnimatedCard visible={true} delay={400}>
                <FollowUpQuestionsSection
                  questions={unifiedQuestions}
                  loading={false}
                  error={unifiedQuestionsError}
                  onRetry={() => {
                    questionsFetchedRef.current = false
                    generateQuestions(textInput || transcript)
                  }}
                  isLocked={false}
                  initialAnswers={unifiedAnswers}
                  onAnswersChange={(ans, det) => {
                    setUnifiedAnswers(ans)
                    setUnifiedDetails(det)
                  }}
                />
              </AnimatedCard>
            )}

            {/* v174.0.1: Submit bubble removed — button at bottom is sufficient */}
          </>
        )}
      </div>

      {/* Bottom Button — changes based on phase */}
      <div className="p-4 pb-6 border-t border-gray-100 bg-white flex-shrink-0">
        {isFollowUp && reportPhase !== 'voice' && scaleItems.length > 0 && !scalesDone ? (
          <button
            data-followup-scales-continue
            onClick={() => scalesReady && setScalesDone(true)}
            disabled={!scalesReady}
            className="w-full py-3.5 rounded-[14px] text-[14px] font-bold flex items-center justify-center gap-2 transition-all border-none cursor-pointer"
            style={{ background: scalesReady ? `linear-gradient(135deg, ${NAVY}, #1E4A6E)` : '#E2E8F0', color: scalesReady ? '#fff' : '#94A3B8' }}>
            <Check size={16} /> {ts('deliver.continueToQuestions', 'Continue')}
          </button>
        ) : isFollowUp && reportPhase === 'questions' && unifiedQuestions?.length > 0 ? (
          <button
            onClick={handleSubmitReport}
            disabled={Object.keys(unifiedAnswers).length === 0 || reportSubmitting}
            className="w-full py-3.5 rounded-[14px] text-[14px] font-bold flex items-center justify-center gap-2 transition-all border-none cursor-pointer"
            style={{
              background: Object.keys(unifiedAnswers).length > 0 && !reportSubmitting
                ? `linear-gradient(135deg, ${NAVY}, #1E4A6E)` : '#E2E8F0',
              color: Object.keys(unifiedAnswers).length > 0 && !reportSubmitting ? '#fff' : '#94A3B8',
            }}>
            {reportSubmitting 
              ? <><OrellaSpinner size={16} tone="inherit" /> {t('followUpReport.submitting', 'Submitting...')}</>
              : <><Send size={16} /> {ts('deliver.submitReportToDoctor', 'Submit Report to Doctor')}</>
            }
          </button>
        ) : (
          <button
            onClick={handleSave}
            disabled={voiceState === 'recording' || voiceState === 'paused' || voiceState === 'transcribing' || !canSave || (isFollowUp && reportPhase !== 'voice')}
            className="w-full py-3.5 rounded-[14px] text-[14px] font-bold flex items-center justify-center gap-2 transition-all border-none cursor-pointer"
            style={{
              background: canSave && voiceState !== 'recording' && voiceState !== 'transcribing' && reportPhase === 'voice'
                ? `linear-gradient(135deg, ${NAVY}, #1E4A6E)` : '#E2E8F0',
              color: canSave && voiceState !== 'recording' && voiceState !== 'transcribing' && reportPhase === 'voice' ? '#fff' : '#94A3B8',
              opacity: (voiceState === 'recording' || voiceState === 'transcribing') ? 0.5 : 1,
            }}>
            {isFollowUp ? <><Check size={16} /> {ts('deliver.continueToQuestions', 'Continue')}</> : <><Check size={16} /> {t('followUpReport.saveReport', 'Save Report')}</>}
          </button>
        )}
      </div>
    </div>,
    document.body
  )
}
